import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import CommentCard from './CommentCard';
import CommentComposer from './CommentComposer';
import ReportModal from './ReportModal';
import { getPostComments, addPostComment, deletePostComment } from '../../services/api';
import { toast } from 'react-toastify';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";

const CommentList = ({ postId, initialCount }) => {
  const { user } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(initialCount || 0);
  
  // Composer state
  const [submitting, setSubmitting] = useState(false);
  
  // Report state
  const [reportingCommentId, setReportingCommentId] = useState(null);
  
  // Validation state
  const [commentError, setCommentError] = useState(null);

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      const response = await getPostComments(postId);
      setComments(response.data || []);
      setNextCursor(response.meta?.nextCursor);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("comment", { post_id: postId });
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    trackEvent("comments_load_more_clicked", { post_id: postId, current_count: comments.length });
    setLoadingMore(true);
    try {
      const response = await getPostComments(postId, nextCursor);
      setComments(prev => [...prev, ...(response.data || [])]);
      setNextCursor(response.meta?.nextCursor);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("comment", { post_id: postId });
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      toast.error('Failed to load more comments');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAddComment = async (body) => {
    setSubmitting(true);
    setCommentError(null);
    try {
      const response = await addPostComment(postId, body);

      // Auto-hide handling
      const autoHidden = response.comment?.status === 'hidden';
      trackEvent("comment_submitted", { post_id: postId, comment_length: body.length, auto_hidden: autoHidden });
      if (autoHidden) {
        setComments(prev => [response.comment, ...prev]);
        toast.success(
          'Your comment contains language that triggers our filter. It has been saved and is awaiting review.',
          { duration: 5000, icon: '🛡️' }
        );
      } else {
        setComments(prev => [response.comment, ...prev]);
        setTotalCount(prev => prev + 1);
        toast.success('Comment posted');
      }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("comment", { post_id: postId });
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      if (error.status === 400 && error.issues) {
        setCommentError(error.issues[0]?.message);
      } else if (error.status === 400) {
        toast.error(error.message);
      } else if (error.status === 429) {
        toast.error(`Slow down. Try again in ${error.retryAfter || 'a few'} seconds.`);
      } else {
        toast.error('Failed to post comment. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    // Optimistic removal
    const previousComments = [...comments];
    const previousCount = totalCount;
    
    setComments(comments.filter(c => c.id !== commentId));
    setTotalCount(Math.max(0, totalCount - 1));

    try {
      await deletePostComment(commentId);
      trackEvent("comment_deleted", { post_id: postId, comment_id: commentId });
      toast.success('Comment deleted');
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        scope.setContext("comment", { post_id: postId });
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      // Rollback
      setComments(previousComments);
      setTotalCount(previousCount);
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-sm" id="comments">
      {/* One heading for the whole card, with how many have been said. */}
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600" aria-hidden="true">
            <MessageCircle size={20} />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-bold leading-tight text-gray-900">Community Discussion</h2>
            <p className="text-[13px] text-gray-500">Ask a question or share your own experience of this trip.</p>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
          {totalCount} {totalCount === 1 ? 'comment' : 'comments'}
        </span>
      </div>

      {/* The composer comes first, so adding a comment never means scrolling past the others. */}
      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
        {user ? (
          <CommentComposer onSubmit={handleAddComment} loading={submitting} error={commentError} />
        ) : (
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-sm text-gray-600">Join the conversation — log in to leave a comment.</p>
            <button
              type="button"
              onClick={() => openAuthModal()}
              className="cursor-pointer rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700"
            >
              Log in to comment
            </button>
          </div>
        )}
      </div>

      <div className="px-2 py-2 sm:px-3">
        {loading ? (
          <div className="space-y-4 p-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 rounded bg-gray-200" />
                  <div className="h-4 w-full rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length > 0 ? (
          <div className="space-y-1">
            {comments.map(comment => (
              <CommentCard
                key={comment.id}
                comment={comment}
                onDelete={handleDelete}
                onReport={(id) => {
                  trackEvent('report_initiated', { target_type: 'comment', target_id: id });
                  setReportingCommentId(id);
                }}
              />
            ))}

            {nextCursor && (
              <div className="flex justify-center pb-2 pt-3">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="cursor-pointer rounded-full bg-teal-50 px-6 py-2.5 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-100"
                >
                  {loadingMore ? 'Loading...' : 'Load more comments'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 py-9 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-500">
              <MessageCircle size={22} />
            </div>
            <h3 className="mb-1 text-[15px] font-semibold text-gray-900">No comments yet</h3>
            <p className="text-sm text-gray-500">Be the first to share your thoughts about this trip.</p>
          </div>
        )}
      </div>


      <ReportModal 
        isOpen={!!reportingCommentId} 
        onClose={() => setReportingCommentId(null)}
        targetId={reportingCommentId}
        targetType="comment"
      />
    </div>
  );
};

export default CommentList;
